import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// --- Config ---
const ANDROID_PACKAGE = "ae.computer_center.deliverit";
const ANDROID_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=ae.computer_center.deliverit";
const IOS_APP_STORE_URL = "https://apps.apple.com/ae/app/deliverit-uae/id1510897598";
const CUSTOM_SCHEME_BASE = "deliverit://"; // Using custom scheme instead of universal links
const MODAL_DISMISS_KEY = "forceAppModalDismissedAt";
const MODAL_HIDE_DURATION_MS = 60 * 60 * 1000; // 1 hour

function isMobile() {
  return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}
function isAndroid() {
  return /android/i.test(navigator.userAgent);
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

// Function to automatically redirect product pages to app
function tryAutoRedirectToApp(path) {
  if (!isMobile()) return false;
  
  // Only auto-redirect for product pages - but show choice modal instead of forcing redirect
  if (path.startsWith('/product/')) {
    // Don't auto-redirect, let the user choose
    return true; // Return true to indicate this is a product page that should show modal
  }
  
  return false;
}

// Function to show choice modal for any page (unified approach)
function showUniversalChoiceModal(path) {
  // Remove any existing modals first
  const existingModal = document.getElementById('deliverit-choice-modal');
  if (existingModal) {
    existingModal.remove();
  }

  const choiceModal = document.createElement('div');
  choiceModal.id = 'deliverit-choice-modal';
  
  // Determine if this is a product page for customized messaging
  const isProductPage = path.startsWith('/product/');
  const title = isProductPage ? "Choose Your Experience" : "Get the Best Experience!";
  const message = isProductPage 
    ? "How would you like to view this product?" 
    : "Use the Deliverit mobile app for a better experience!";
  
  choiceModal.innerHTML = `
    <div id="deliverit-choice-backdrop" style="
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.8); display: flex; align-items: center;
      justify-content: center; z-index: 10000; padding: 20px;
    ">
      <div style="
        background: white; border-radius: 12px; padding: 32px;
        max-width: 380px; text-align: center; box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      ">
        <img src="${require("../assets/image/layout/logo-header.png")}" 
             alt="Deliverit" style="width: 80px; margin-bottom: 16px;" />
        <h3 style="margin-bottom: 12px; color: #333; font-size: 20px;">
          ${title}
        </h3>
        <p style="margin-bottom: 24px; color: #666; line-height: 1.5; font-size: 16px;">
          ${message}
        </p>
        
        <div style="display: flex; flex-direction: column; gap: 12px;">
          <button id="open-in-app-btn" data-path="${path}" style="
            padding: 14px 24px; background: linear-gradient(90deg, #0db2ad, #005d6d);
            color: white; border: none; border-radius: 8px; cursor: pointer;
            font-weight: bold; font-size: 16px;
          ">📱 Open in Deliverit App</button>
          
          <button id="continue-web-btn" style="
            padding: 14px 24px; border: 1px solid #ddd; background: white;
            color: #333; border-radius: 8px; cursor: pointer; font-size: 16px;
          ">🌐 Continue on Web</button>
        </div>
        
        <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #eee;">
          <p style="font-size: 14px; color: #999; margin-bottom: 8px;">
            Don't have the app?
          </p>
          <a href="${isIOS() ? IOS_APP_STORE_URL : ANDROID_PLAY_STORE_URL}" 
             target="_blank" rel="noopener noreferrer" 
             style="color: #0db2ad; text-decoration: underline; font-size: 14px;">
            ${isIOS() ? '📱 Download on App Store' : '📱 Get it on Google Play'}
          </a>
        </div>
      </div>
    </div>
  `;
  
  document.body.appendChild(choiceModal);
  
  // Add event listeners
  document.getElementById('open-in-app-btn').addEventListener('click', function() {
    const path = this.getAttribute('data-path');
    let appPath = path.replace(/^\//, ''); // Remove leading slash
    
    // For product pages, convert to the format your app expects
    if (appPath.startsWith('product/')) {
      // Keep the product path as is: product/some-slug-203
      appPath = appPath;
    } else if (appPath === '' || appPath === 'home') {
      // For home page, just use the base scheme
      appPath = '';
    }
    
    const customSchemeUrl = `${CUSTOM_SCHEME_BASE}${appPath}`;
    console.log('User chose to open in app:', customSchemeUrl);
    
    // Try to open the app
    window.location.href = customSchemeUrl;
    
    // If app doesn't open within 2.5 seconds, show install prompt
    setTimeout(() => {
      if (document.hasFocus()) {
        document.getElementById('deliverit-choice-modal')?.remove();
        showAppInstallPrompt();
      } else {
        // App opened successfully, remove modal and remember choice
        document.getElementById('deliverit-choice-modal')?.remove();
      
      }
    }, 2500);
  });
  
  document.getElementById('continue-web-btn').addEventListener('click', function() {
    document.getElementById('deliverit-choice-modal')?.remove();
    localStorage.setItem(MODAL_DISMISS_KEY, Date.now().toString());
  });
  
  // Allow clicking backdrop to close modal
  document.getElementById('deliverit-choice-backdrop').addEventListener('click', function(e) {
    if (e.target === this) {
      document.getElementById('deliverit-choice-modal')?.remove();
      localStorage.setItem(MODAL_DISMISS_KEY, Date.now().toString());
    }
  });
}

// Function to show app install prompt when app is not installed
function showAppInstallPrompt() {
  // Remove any existing install prompts first
  const existingPrompt = document.getElementById('deliverit-install-prompt');
  if (existingPrompt) {
    existingPrompt.remove();
  }

  const installPrompt = document.createElement('div');
  installPrompt.id = 'deliverit-install-prompt';
  installPrompt.innerHTML = `
    <div id="deliverit-install-backdrop" style="
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.8); display: flex; align-items: center;
      justify-content: center; z-index: 10000; padding: 20px;
    ">
      <div style="
        background: white; border-radius: 12px; padding: 24px;
        max-width: 350px; text-align: center; box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      ">
        <img src="${require("../assets/image/layout/logo-header.png")}" 
             alt="Deliverit" style="width: 80px; margin-bottom: 16px;" />
        <h3 style="margin-bottom: 12px; color: #333;">App Required</h3>
        <p style="margin-bottom: 20px; color: #666; line-height: 1.5;">
          To view this content, please install the Deliverit app.
        </p>
        <div style="display: flex; gap: 12px; justify-content: center;">
          <button id="stay-web-btn" style="
            padding: 12px 20px; border: 1px solid #ddd; background: white;
            border-radius: 6px; cursor: pointer;
          ">Stay on Web</button>
          <a href="${isIOS() ? IOS_APP_STORE_URL : ANDROID_PLAY_STORE_URL}" style="
            padding: 12px 20px; background: #0db2ad; color: white;
            border-radius: 6px; text-decoration: none; display: inline-block;
          ">Install App</a>
        </div>
      </div>
    </div>
  `;
  
  document.body.appendChild(installPrompt);
  
  // Add event listener for stay on web button
  document.getElementById('stay-web-btn').addEventListener('click', function() {
    document.getElementById('deliverit-install-prompt')?.remove();
  });
  
  // Allow clicking backdrop to close modal
  document.getElementById('deliverit-install-backdrop').addEventListener('click', function(e) {
    if (e.target === this) {
      document.getElementById('deliverit-install-prompt')?.remove();
    }
  });
}

export default function ForceAppModal({ path }) {
  const [autoRedirectAttempted, setAutoRedirectAttempted] = useState(false);

  useEffect(() => {
    if (!isMobile()) return;
    
    // Check if modal was dismissed recently
    const dismissedAt = localStorage.getItem(MODAL_DISMISS_KEY);
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < MODAL_HIDE_DURATION_MS) {
      return; // Don't show any modal if recently dismissed
    }
    
    // Show unified modal for all pages (product and non-product)
    if (!autoRedirectAttempted) {
      setAutoRedirectAttempted(true);
      showUniversalChoiceModal(path);
    }
  }, [path, autoRedirectAttempted]);

  // This component no longer renders any React JSX - everything is handled by the unified modal
  return null;
}

ForceAppModal.propTypes = {
  path: PropTypes.string.isRequired,
};
